import React, { useState, useEffect } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Button } from "@stackje/shared-components/ui/button";
import { Wand2 } from "lucide-react";
import { useToast } from "@stackje/shared-components/hooks/useToast";

interface CustomColorPickerProps {
  onColorSchemeSelect: (colors: string[]) => void;
  onCancel: () => void;
  initialColors?: string[];
}

export const CustomColorPicker: React.FC<CustomColorPickerProps> = ({
  onColorSchemeSelect,
  onCancel,
  initialColors,
}) => {
  const [editingColorIndex, setEditingColorIndex] = useState<number | null>(null);
  const [customColors, setCustomColors] = useState<string[]>(
    initialColors || [
      '#f8fafc', // Light background
      '#3b82f6', // Accent/button
      '#1e40af', // Primary/card
      '#1e293b', // Dark
      '#0f172a', // Text/foreground
    ]
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  // Auto-focus input when color picker expands (click color square)
  useEffect(() => {
    if (editingColorIndex !== null) {
      // Small delay to ensure the animation starts before focusing
      setTimeout(() => {
        const input = document.querySelector(`[data-color-input="${editingColorIndex}"] input`) as HTMLInputElement;
        if (input && document.activeElement !== input) {
          input.focus();
        }
      }, 100);
    }
  }, [editingColorIndex]);

  const handleColorEdit = (index: number, newColor: string) => {
    const updatedColors = [...customColors];
    updatedColors[index] = newColor;
    setCustomColors(updatedColors);
  };

  const generateColorPalette = async () => {
    if (isGenerating) return;

    // Validate base color before sending request
    const baseColor = customColors[0];
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (!baseColor || !hexColorRegex.test(baseColor)) {
      toast({
        title: "Invalid base color",
        description: "Please enter a valid hex color (e.g., #ff0000) before generating a palette.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch('/api/generate-color-palette', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          baseColor: baseColor,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 429) {
          // Rate limited
          const resetTime = response.headers.get('X-RateLimit-Reset');
          const message = resetTime
            ? `Rate limit exceeded. Try again in ${resetTime} seconds.`
            : 'Too many requests. Please wait before trying again.';

          toast({
            title: "Rate limit exceeded",
            description: message,
            variant: "destructive",
          });
          return;
        }

        throw new Error(errorData.error || 'Failed to generate color palette');
      }

      const data = await response.json();
      if (data.colors && Array.isArray(data.colors)) {
        setCustomColors(data.colors);
        toast({
          title: "Color palette generated!",
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Color generation error:', error);

      // Don't show error toast if we already handled rate limiting
      if (error instanceof Error && !error.message.includes('Rate limit')) {
        toast({
          title: "Generation failed",
          description: "Unable to generate color palette. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const applyColorScheme = () => {
    onColorSchemeSelect(customColors);
  };

  return (
    <div className="space-y-4">
      {/* Color Rows */}
      <div className="space-y-3">
        {customColors.map((color, index) => (
          <div key={index} className="space-y-2">
            {/* Color Row */}
            <div className="flex items-center gap-3">
              <button
                className="w-12 h-12 rounded-lg border-2 border-gray-300 transition-transform hover:scale-105 active:scale-95 shrink-0"
                style={{ backgroundColor: color }}
                onClick={() => setEditingColorIndex(editingColorIndex === index ? null : index)}
              />
              <div data-color-input={index} className="flex-1">
                <HexColorInput
                  color={color}
                  onChange={(newColor) => handleColorEdit(index, newColor)}
                  onFocus={() => setEditingColorIndex(index)}
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 placeholder-gray-400 font-mono"
                  placeholder="#000000"
                  prefixed={true}
                />
              </div>
              {index === 0 && (
                <button
                  onClick={generateColorPalette}
                  disabled={isGenerating}
                  className="w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center shrink-0"
                  title="Generate AI color palette"
                >
                  <Wand2 className={`w-4 h-4 text-gray-600 ${isGenerating ? 'animate-spin' : ''}`} />
                </button>
              )}
            </div>

            {/* Expandable Color Picker */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                editingColorIndex === index
                  ? 'max-h-40 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pt-2">
                <HexColorPicker
                  color={color}
                  onChange={(newColor) => handleColorEdit(index, newColor)}
                  style={{ width: '100%', height: '120px' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-4">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button onClick={applyColorScheme} className="flex-1">
          ✓ Apply
        </Button>
      </div>
    </div>
  );
};